import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../context/ThemeContext';
import { PLACEHOLDER_IMAGES } from '../config/placeholders';

interface VehicleData {
  [key: string]: any;
}

interface StatsData {
  count?: number;
  avg_days_between?: number;
  price_stats?: {
    min: number;
    max: number;
    avg: number;
    median: number;
  };
  min?: number;
  max?: number;
  avg?: number;
  percentiles?: Record<string, number>;
}

const VehicleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();

  // KEY FIX: use bundler-processed import instead of the raw public-path string
  // from tenant config, which 404s on Amplify when assets live in src/assets/.
  const placeholderImage = PLACEHOLDER_IMAGES[tenant.id] ?? PLACEHOLDER_IMAGES.porsche;

  // Read the vehicle directly from router state passed by CarCard.
  // Falls back to null if user lands directly on the URL (e.g. shared link).
  const vehicleFromState: VehicleData | null = location.state?.vehicle ?? null;

  const [vehicle, setVehicle] = useState<VehicleData | null>(vehicleFromState);
  const [loading, setLoading] = useState<boolean>(!vehicleFromState);
  const [copied, setCopied] = useState<string | null>(null);
  const [exactStats, setExactStats] = useState<StatsData | null>(null);
  const [similarStats, setSimilarStats] = useState<StatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Only fetch from backend if vehicle wasn't passed via router state
  // (e.g. user opened /vehicle/:id directly or refreshed the page)
  useEffect(() => {
    if (vehicleFromState || !id) return;

    const abortController = new AbortController();
    setLoading(true);

    fetch(`https://mynextclassic-backend.net/listing?id=${id}&brand=${tenant.id}`, {
      signal: abortController.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const result = data?.results?.[0] ?? data ?? null;
        setVehicle(result);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch vehicle', err);
        }
      })
      .finally(() => setLoading(false));

    return () => abortController.abort();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch market stats once vehicle is available
  useEffect(() => {
    if (!vehicle) return;
    const configHashExact = vehicle.config_hash_exact?.raw;
    const configHashSimilar = vehicle.config_hash_similar?.raw;
    if (!configHashExact) return;

    const abortController = new AbortController();
    let isActive = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const baseUrl = 'https://mynextclassic-backend.net';

        const [exactRes, similarRes] = await Promise.all([
          fetch(`${baseUrl}/exact-stats?config_hash=${configHashExact}&brand=${tenant.id}`, {
            signal: abortController.signal,
          }),
          configHashSimilar
            ? fetch(`${baseUrl}/similar-stats?config_hash=${configHashSimilar}&brand=${tenant.id}`, {
                signal: abortController.signal,
              })
            : Promise.resolve(null),
        ]);

        if (!isActive) return;

        const exact = await exactRes.json();
        setExactStats(exact);

        if (similarRes) {
          const similar = await similarRes.json();
          setSimilarStats(similar);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Failed to fetch stats', error);
      } finally {
        if (isActive) setStatsLoading(false);
      }
    };

    fetchStats();
    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [vehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  const getRaw = (field: string): string => {
    if (!vehicle) return '\u2014';
    const val = vehicle[field]?.raw;
    if (val === undefined || val === null || val === '') return '\u2014';
    const lowerVal = String(val).toLowerCase();
    if (lowerVal === 'other' || lowerVal === 'unknown') return '\u2014';
    return String(val);
  };

  const formatEuro = (val: number | null | undefined): string => {
    if (val == null || val === 0) return '\u2014';
    return val.toLocaleString('de-DE') + '\u20AC';
  };

  const formatPriceDisplay = (): string => {
    const p = vehicle?.listing_price_value?.raw;
    const c = vehicle?.listing_price_currency?.raw || 'EUR';
    if (!p || p <= 0) return '\u2014';
    const sym = c === 'EUR' ? 'EUR' : c === 'USD' ? 'USD' : c;
    return `${sym} ${Number(p).toLocaleString('de-DE')}`;
  };

  const formatMileage = (): string => {
    const val = vehicle?.listing_mileage_value?.raw;
    if (!val || val <= 0) return '\u2014';
    return `${Number(val).toLocaleString('de-DE')} KM`;
  };

  const [imageStatus, setImageStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [validatedImageUrl, setValidatedImageUrl] = useState<string | null>(null);
  const originalImageUrl = vehicle?.listing_image_url?.raw || '';

  useEffect(() => {
    if (!originalImageUrl) {
      setImageStatus('invalid');
      return;
    }

    const img = new Image();
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const MIN_WIDTH = 300;
    const MIN_HEIGHT = 200;

    const rejectedDomains = [
      'goo-net-exchange.com', 'barrett-jackson.com', 'overtonautomotive.com',
      'europeancollectibles.com', 'automotive-auctions.nl', 'mecum.com',
      'crossley-webb.com', '911uk.com', 'autoluce.com', 'mechatronik.de',
      'postwarclassic.com', 'theoctanecollection.com', 'mitula.net',
      'rennlist.com', 'driving.ca', 'hofarts.com', 'doubleclick.net',
      'topgeargreece.gr', 'beverlyhillscarclub.com', 'ibsrv.net', 'i.ebayimg.com',
    ];

    const badFilePatterns = [
      'logo', 'logos', 'icon', 'icons', 'favicon', 'notification', 'graph',
      'marque', 'brand', 'badge', 'symbol', 'emblema', 'fb_image', 'site_logo',
      'header-logo', 'full-logo', 'cropped-', 'ograph', 'wordmarque',
    ];

    const lowerUrl = originalImageUrl.toLowerCase();
    let urlObj;
    try {
      urlObj = new URL(lowerUrl);
    } catch (e) {
      setImageStatus('invalid');
      return;
    }

    const hostname = urlObj.hostname;
    const pathname = urlObj.pathname;
    const filename = (pathname.split('/').pop() || '').split('?')[0].split('#')[0];

    const isRejectedDomain = rejectedDomains.some((domain) => hostname.includes(domain));
    const isBadFile = badFilePatterns.some((pattern) => filename.includes(pattern));
    const isSpecialCase =
      lowerUrl.startsWith('data:') || filename.endsWith('.svg') || filename.endsWith('.gif');

    if (isRejectedDomain || isBadFile || isSpecialCase) {
      setImageStatus('invalid');
      return;
    }

    img.onload = () => {
      if (!isMounted) return;
      clearTimeout(timeoutId);
      if (img.naturalWidth < MIN_WIDTH || img.naturalHeight < MIN_HEIGHT) {
        setImageStatus('invalid');
      } else {
        setImageStatus('valid');
        setValidatedImageUrl(originalImageUrl);
      }
    };

    img.onerror = () => {
      if (isMounted) {
        clearTimeout(timeoutId);
        setImageStatus('invalid');
      }
    };

    img.src = originalImageUrl;
    timeoutId = setTimeout(() => {
      if (isMounted) setImageStatus('invalid');
    }, 2500);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [originalImageUrl]);

  const displayImageUrl =
    imageStatus === 'valid' && validatedImageUrl ? validatedImageUrl : placeholderImage;

  if (loading) {
    return (
      <div className="min-h-screen bg-mp-bg flex items-center justify-center">
        <p className="font-mono text-mp-muted text-sm animate-pulse">
          &gt; LOADING DOSSIER...
        </p>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-mp-bg flex items-center justify-center flex-col gap-4">
        <p className="font-mono text-mp-muted text-sm">&gt; VEHICLE NOT FOUND</p>
        <button
          onClick={handleBack}
          className="font-mono text-xs text-mp-muted hover:text-mp-text transition-colors underline"
        >
          Return to Results
        </button>
      </div>
    );
  }

  const year = getRaw('listing_production_year');
  const generation = getRaw('listing_model_generation_txt');
  const variant = getRaw('listing_pab_variant_txt');
  const titleParts = [
    year !== '\u2014' ? year : '',
    generation,
    variant !== '\u2014' ? variant : '',
  ].filter(Boolean);
  const title = titleParts.join(' ') || 'Vehicle Dossier';

  const telemetryRows = [
    { label: 'MAKE', value: getRaw('listing_make_txt') },
    { label: 'MODEL', value: getRaw('listing_model_txt') },
    { label: 'GENERATION', value: getRaw('listing_model_generation_txt') },
    { label: 'VARIANT', value: getRaw('listing_pab_variant_txt') },
    { label: 'BODY TYPE', value: getRaw('listing_body_type_txt') },
    { label: 'BUILD', value: getRaw('listing_build') },
    { label: 'ENGINE', value: getRaw('listing_engine_txt') },
    { label: 'TRANSMISSION', value: getRaw('listing_transmission_txt') },
    {
      label: 'OUTPUT (PS)',
      value: getRaw('listing_ps') !== '\u2014' ? `${getRaw('listing_ps')} PS` : '\u2014',
    },
    { label: 'MILEAGE', value: formatMileage() },
    { label: 'PRODUCTION YEAR', value: getRaw('listing_production_year') },
    { label: 'PRICE', value: formatPriceDisplay() },
    { label: 'EXTERIOR COLOR', value: getRaw('listing_grouped_exterior_color') },
    { label: 'EXTERIOR COLOR (DETAIL)', value: getRaw('listing_exterior_color_txt') },
    { label: 'BASE COLOR', value: getRaw('listing_grouped_base_color') },
    { label: 'INTERIOR COLOR', value: getRaw('listing_interior_base_color') },
    { label: 'INTERIOR COLOR (DETAIL)', value: getRaw('listing_interior_color_txt') },
    { label: 'LOCATION', value: getRaw('listing_location_txt') },
    { label: 'CITY', value: getRaw('listing_city_txt') },
    { label: 'COUNTRY', value: getRaw('listing_country_txt') },
    { label: 'COUNTRY CODE', value: getRaw('listing_country_code_txt') },
    { label: 'ROAD LEGAL', value: getRaw('listing_road_legal') },
    { label: 'CLASSIC CAR (H)', value: getRaw('listing_classic_car') },
    { label: 'EXPERT APPRAISAL', value: getRaw('listing_expert_appraisal') },
    { label: 'PORSCHE CUP', value: getRaw('listing_porsche_cup') },
    { label: 'CUP DETAILS', value: getRaw('listing_porsche_cup_details') },
    { label: 'FIA', value: getRaw('listing_fia') },
    { label: 'FIA DETAILS', value: getRaw('listing_fia_details') },
    { label: 'ACO', value: getRaw('listing_aco') },
    { label: 'ACO DETAILS', value: getRaw('listing_aco_details') },
    { label: 'ALREADY SOLD', value: getRaw('listing_already_sold') },
  ].filter((row) => row.value !== '\u2014');

  return (
    <div className="min-h-screen bg-mp-bg text-mp-text font-body antialiased">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 flex items-center justify-between border-b border-mp-border bg-mp-bg/90 backdrop-blur-sm px-6 py-4">
        <button
          onClick={handleBack}
          className="flex items-center gap-3 text-mp-muted hover:text-mp-text transition-colors group"
        >
          <span className="material-symbols-outlined text-xl group-hover:-translate-x-1 transition-transform">
            arrow_back
          </span>
          <span className="font-mono text-sm tracking-widest uppercase">Return to Results</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-mp-muted border border-mp-border px-2 py-1 bg-mp-surface">
            REF //{' '}
            {getRaw('listing_id') !== '\u2014'
              ? getRaw('listing_id').slice(0, 12)
              : generation}
          </span>
        </div>
      </header>

      <main className="flex flex-col lg:flex-row min-h-screen pt-[65px]">
        {/* Left: Image (60%) */}
        <section className="w-full lg:w-[60%] h-[400px] lg:h-[calc(100vh-65px)] lg:sticky lg:top-[65px] overflow-y-auto gallery-snap bg-black border-r border-mp-border">
          {imageStatus !== 'loading' ? (
            <div className="h-full w-full relative flex items-center justify-center bg-mp-surface snap-start">
              <img
                alt={title}
                className="object-cover w-full h-full opacity-90 hover:opacity-100 transition-opacity duration-500"
                src={displayImageUrl}
              />
              <div className="absolute bottom-6 left-6 font-mono text-xs text-mp-text bg-black/50 px-3 py-1 border border-mp-border backdrop-blur-md">
                FIG 01 // EXTERIOR
              </div>
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-mp-surface">
              <span className="font-mono text-mp-muted text-sm">NO IMAGES AVAILABLE</span>
            </div>
          )}
        </section>

        {/* Right: Data Pane (40%) */}
        <section className="w-full lg:w-[40%] overflow-y-auto bg-mp-bg p-8 lg:p-12 flex flex-col gap-10">
          {/* Spec Header */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {getRaw('listing_already_sold') === 'Yes' && (
                <span className="flex items-center gap-1 font-mono text-[10px] text-primary border border-primary/30 bg-primary/10 px-2 py-1">
                  <span className="material-symbols-outlined text-[12px]">sell</span>
                  SOLD
                </span>
              )}
              {getRaw('listing_road_legal') === 'Yes' && (
                <span className="flex items-center gap-1 font-mono text-[10px] text-mp-accent border border-mp-accent bg-mp-accent/10 px-2 py-1">
                  <span className="material-symbols-outlined text-[12px]">verified</span>
                  ROAD LEGAL
                </span>
              )}
              {getRaw('listing_classic_car') === 'Yes' && (
                <span className="flex items-center gap-1 font-mono text-[10px] text-mp-accent border border-mp-accent bg-mp-accent/10 px-2 py-1">
                  <span className="material-symbols-outlined text-[12px]">directions_car</span>
                  CLASSIC CAR (H)
                </span>
              )}
            </div>

            <h1 className="font-display text-3xl lg:text-[42px] leading-[1.1] font-semibold text-mp-text uppercase tracking-tight">
              {title}
            </h1>
            <p className="font-body text-base text-mp-muted leading-relaxed max-w-md">
              {getRaw('listing_body_type_txt') !== '\u2014' ? getRaw('listing_body_type_txt') : ''}
              {getRaw('listing_transmission_txt') !== '\u2014'
                ? ` \u2022 ${getRaw('listing_transmission_txt')}`
                : ''}
              {getRaw('listing_engine_txt') !== '\u2014'
                ? ` \u2022 ${getRaw('listing_engine_txt')}`
                : ''}
            </p>

            {vehicle?.listing_url?.raw ? (
              <a
                href={vehicle.listing_url.raw}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-14 bg-primary hover:bg-primary-hover text-mp-text font-display font-medium text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2 mt-2"
              >
                <span className="material-symbols-outlined text-lg">open_in_new</span>
                View Original Listing
              </a>
            ) : (
              <div className="w-full h-14 bg-mp-surface border border-mp-border text-mp-muted font-display font-medium text-sm uppercase tracking-widest flex items-center justify-center gap-2 mt-2">
                <span className="material-symbols-outlined text-lg">block</span>
                Listing Unavailable
              </div>
            )}
          </div>

          {/* Telemetry Table */}
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-lg text-mp-text border-b border-mp-border pb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-mp-muted text-xl">tune</span>
              Telemetry Data
            </h2>
            <div className="flex flex-col font-mono text-[13px]">
              {telemetryRows.map((row) => (
                <div
                  key={row.label}
                  className="flex justify-between py-2.5 border-b border-mp-border hover:bg-mp-surface transition-colors px-2 cursor-pointer"
                  onClick={() => handleCopy(row.value, row.label)}
                >
                  <span className="text-mp-muted text-[11px]">{row.label}</span>
                  <span
                    className={`text-mp-text text-[12px] ${copied === row.label ? 'text-mp-accent' : ''}`}
                  >
                    {copied === row.label ? 'COPIED' : row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Market Intelligence */}
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-lg text-mp-text border-b border-mp-border pb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-mp-muted text-xl">analytics</span>
              Market Intelligence
            </h2>

            {statsLoading && (
              <p className="font-mono text-mp-muted text-xs animate-pulse">
                &gt; LOADING MARKET DATA...
              </p>
            )}

            {exactStats && exactStats.count && exactStats.count > 0 && (
              <div className="border border-mp-border bg-mp-surface p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-sm text-mp-text uppercase tracking-wide flex items-center gap-2">
                    <span className="material-symbols-outlined text-mp-accent text-[16px]">
                      bar_chart
                    </span>
                    Exact Matches (24m)
                  </h3>
                  <span className="font-mono text-lg font-bold text-mp-accent">
                    {exactStats.count}
                  </span>
                </div>
                <div className="font-mono text-[12px] flex flex-col gap-1.5">
                  {exactStats.avg_days_between && (
                    <div className="flex justify-between">
                      <span className="text-mp-muted">AVG WAIT</span>
                      <span className="text-mp-text">{exactStats.avg_days_between} days</span>
                    </div>
                  )}
                  {exactStats.price_stats && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-mp-muted">PRICE RANGE</span>
                        <span className="text-mp-text">
                          {formatEuro(exactStats.price_stats.min)} &ndash;{' '}
                          {formatEuro(exactStats.price_stats.max)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-mp-muted">AVERAGE</span>
                        <span className="text-mp-text">
                          {formatEuro(exactStats.price_stats.avg)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-mp-muted">MEDIAN</span>
                        <span className="text-mp-text">
                          {formatEuro(exactStats.price_stats.median)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => navigate('/', {
                    state: {
                      historyHash: vehicle.config_hash_exact?.raw,
                      historyTitle: title,
                    }
                  })}
                  className="mt-2 flex items-center gap-1 font-mono text-[10px] text-primary hover:text-mp-text transition-colors uppercase tracking-wider"
                >
                  <span className="material-symbols-outlined text-[12px]">history</span>
                  View full car history
                </button>
              </div>
            )}

            {similarStats && similarStats.count && similarStats.count > 0 && (
              <div className="border border-mp-border bg-mp-surface p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-sm text-mp-text uppercase tracking-wide flex items-center gap-2">
                    <span className="material-symbols-outlined text-mp-accent text-[16px]">
                      trending_up
                    </span>
                    Similar Cars (Variant + HP)
                  </h3>
                  <span className="font-mono text-lg font-bold text-mp-accent">
                    {similarStats.count}
                  </span>
                </div>
                <div className="font-mono text-[12px] flex flex-col gap-1.5">
                  {similarStats.min != null && similarStats.max != null && (
                    <div className="flex justify-between">
                      <span className="text-mp-muted">PRICE RANGE</span>
                      <span className="text-mp-text">
                        {formatEuro(similarStats.min)} &ndash; {formatEuro(similarStats.max)}
                      </span>
                    </div>
                  )}
                  {similarStats.avg != null && (
                    <div className="flex justify-between">
                      <span className="text-mp-muted">AVERAGE</span>
                      <span className="text-mp-text">{formatEuro(similarStats.avg)}</span>
                    </div>
                  )}
                  {similarStats.percentiles && (
                    <div className="flex flex-col gap-1 mt-1 pt-2 border-t border-mp-border">
                      <span className="text-mp-muted text-[10px] uppercase tracking-widest">
                        Percentiles
                      </span>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {Object.entries(similarStats.percentiles).map(([key, val]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-mp-muted text-[10px]">P{key}</span>
                            <span className="text-mp-text text-[11px]">{formatEuro(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!statsLoading && !exactStats && !similarStats && (
              <p className="font-mono text-mp-muted text-xs">
                &gt; No market data available for this configuration
              </p>
            )}
          </div>

          {/* Listing Timeline */}
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-lg text-mp-text border-b border-mp-border pb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-mp-muted text-xl">history</span>
              Listing Timeline
            </h2>
            {getRaw('listing_date_publication_txt') !== '\u2014' ||
            getRaw('listing_date_firstindex') !== '\u2014' ||
            getRaw('listing_date_lastindex') !== '\u2014' ||
            getRaw('scraped_at') !== '\u2014' ? (
              <div className="relative border-l border-mp-border ml-2 flex flex-col gap-6 font-mono text-sm">
                {getRaw('listing_date_publication_txt') !== '\u2014' && (
                  <div className="relative pl-6">
                    <div className="absolute w-2 h-2 bg-mp-text -left-[4.5px] top-1.5 outline outline-4 outline-mp-bg" />
                    <div className="text-mp-muted text-xs mb-1">PUBLISHED</div>
                    <div className="text-mp-text">{getRaw('listing_date_publication_txt')}</div>
                  </div>
                )}
                {getRaw('listing_date_firstindex') !== '\u2014' && (
                  <div className="relative pl-6">
                    <div className="absolute w-2 h-2 bg-mp-surface border border-mp-border -left-[4.5px] top-1.5 outline outline-4 outline-mp-bg" />
                    <div className="text-mp-muted text-xs mb-1">FIRST INDEXED</div>
                    <div className="text-mp-text">{getRaw('listing_date_firstindex')}</div>
                  </div>
                )}
                {getRaw('listing_date_lastindex') !== '\u2014' && (
                  <div className="relative pl-6">
                    <div className="absolute w-2 h-2 bg-primary -left-[4.5px] top-1.5 outline outline-4 outline-mp-bg" />
                    <div className="text-primary text-xs mb-1">LAST INDEXED</div>
                    <div className="text-mp-text">{getRaw('listing_date_lastindex')}</div>
                  </div>
                )}
                {getRaw('scraped_at') !== '\u2014' && (
                  <div className="relative pl-6">
                    <div className="absolute w-2 h-2 bg-mp-accent -left-[4.5px] top-1.5 outline outline-4 outline-mp-bg" />
                    <div className="text-mp-accent text-xs mb-1">REVIEWED AT</div>
                    <div className="text-mp-text">{getRaw('scraped_at')}</div>
                  </div>
                )}
              </div>
            ) : (
              <p className="font-mono text-mp-muted text-xs px-2 italic">
                &gt; No specific timeline events available for this listing record.
                {exactStats && exactStats.count && exactStats.count > 0 && (
                  <span className="block mt-1">
                    Historically, {exactStats.count} similar records were identified.
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="pb-12" />
        </section>
      </main>
    </div>
  );
};

export default VehicleDetail;
